import React from "react";
import { Globe2, Users, Lock } from "lucide-react";
import { SECTIONS } from "@/constants/testIds";

const LEVELS = [
  {
    icon: Globe2,
    tag: "Open",
    title: "Public Information",
    body:
      "Knowledge approved by community for public sharing — species, landscapes, ecological context.",
    tint: "#6B7A6A",
  },
  {
    icon: Users,
    tag: "Internal",
    title: "Community Information",
    body:
      "Information held by, and shared within, community — language, ranger detail, planning data.",
    tint: "#A95A3F",
  },
  {
    icon: Lock,
    tag: "Restricted",
    title: "Restricted Cultural Information",
    body:
      "Sacred and sensitive knowledge that remains with Traditional Owners. Never digitised without consent.",
    tint: "#2C3C2B",
  },
];

export default function CommunityAuthority() {
  return (
    <section
      id="authority"
      data-testid={SECTIONS.authority}
      className="relative py-24 md:py-32 bg-[#EBE4D8] overflow-hidden"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 opacity-25 pointer-events-none">
        <img
          src="https://images.unsplash.com/photo-1618661733134-6a750e4aa08a?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#EBE4D8]/80" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="bm-eyebrow mb-6">Section 09 — Governance</div>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-[#2D2621]">
            Community Authority
          </h2>
          <p className="mt-6 text-[#584D45] text-lg leading-relaxed">
            Communities decide what information is shared, what remains
            restricted and how knowledge is represented. The process respects
            Traditional Owner authority and cultural governance at every step.
          </p>
        </div>

        {/* Three-level diagram */}
        <div className="mt-16 relative">
          <div className="absolute left-8 top-12 bottom-12 w-px bg-[#C5B6A3] hidden md:block" />
          <ol className="space-y-6 md:space-y-8">
            {LEVELS.map(({ icon: Icon, tag, title, body, tint }, i) => (
              <li
                key={title}
                className="relative md:pl-24 group"
              >
                {/* node */}
                <div
                  className="absolute left-0 md:left-0 top-0 w-16 h-16 rounded-full flex items-center justify-center text-white shadow-md ring-4 ring-[#EBE4D8]"
                  style={{ backgroundColor: tint }}
                >
                  <Icon size={22} />
                </div>

                <div className="md:ml-0 ml-0 pl-20 md:pl-0 bm-card bg-[#FDFBF7] p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-4 md:gap-10">
                  <div className="md:w-44 shrink-0">
                    <div
                      className="text-[11px] tracking-[0.28em] uppercase font-semibold"
                      style={{ color: tint }}
                    >
                      Level {i + 1} · {tag}
                    </div>
                    <h3 className="font-serif-display text-2xl md:text-3xl text-[#2D2621] mt-2 leading-tight">
                      {title}
                    </h3>
                  </div>
                  <p className="text-[#584D45] leading-relaxed flex-1">{body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-14 max-w-3xl border-l-2 border-[#A95A3F] pl-6">
          <p className="font-serif-display italic text-2xl text-[#2C3C2B] leading-snug">
            Nothing is shared, published or digitised without explicit
            community approval. Cultural authority sits with Traditional
            Owners — always.
          </p>
        </div>
      </div>
    </section>
  );
}
